import Button from "@cloudscape-design/components/button"
import Input from "@cloudscape-design/components/input"
import { ProtocolVisit } from "../../../services/studyProtocolApi"
import { VisitField } from "./useAssessmentsGrid"

interface VisitMetaCellProps {
  /**
   * The visit data containing the field value.
   */
  visit: ProtocolVisit

  /**
   * Which field of the visit to display/edit.
   */
  field: VisitField

  /**
   * Placeholder text shown when editing an empty field.
   */
  placeholder: string

  /**
   * Whether this specific cell is currently being edited.
   */
  editing: boolean

  /**
   * Draft value while editing (uncommitted).
   */
  draft: string

  /**
   * Whether this visit's column is currently being hovered.
   */
  hovering: boolean

  /**
   * Callback when the cell is clicked to start editing.
   */
  onStartEdit: () => void

  /**
   * Callback when the draft value changes.
   */
  onDraftChange: (value: string) => void

  /**
   * Callback to commit the draft (check mark / Enter)
   */
  onCommit: () => void

  /**
   * Callback to discard the draft (X / Escape)
   */
  onCancel: () => void

  /**
   * Callback when hover state changes (null to clear hover)
   */
  onHover: (visitId: string | null) => void

  /**
   * When true, disables all editing interactions.
   */
  readOnly?: boolean
}

/**
 * Renders a single cell in the visit metadata rows (Visit, Study Week, Study Day, Window).
 *
 * Clicking a cell edits just that cell: the input gets an active (blue) outline and a small
 * confirm/cancel overlay anchored to the cell's bottom-right corner.
 */
const VisitMetaCell = ({
  visit,
  field,
  placeholder,
  editing,
  draft,
  hovering,
  onStartEdit,
  onDraftChange,
  onCommit,
  onCancel,
  onHover,
  readOnly = false,
}: VisitMetaCellProps) => {
  const value = visit[field] || ""
  const isVisitRow = field === "visit_number"
  const Tag = isVisitRow ? "th" : "td"

  const className = [
    isVisitRow ? "visit-cell" : "",
    editing ? "cell-editing" : "",
    hovering && !editing ? "hovering" : "",
    readOnly ? "readonly" : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <Tag
      className={className}
      onMouseEnter={() => !readOnly && !editing && onHover(visit.id)}
      onMouseLeave={() => !readOnly && onHover(null)}
      onClick={() => !readOnly && !editing && onStartEdit()}
    >
      {editing ? (
        <>
          <Input
            value={draft}
            onChange={({ detail }) => onDraftChange(detail.value)}
            onKeyDown={(event) => {
              if (event.detail.key === "Enter") {
                // Block the browser's implicit form submission (the grid lives inside the
                // protocol <form>); Enter only commits this cell.
                event.preventDefault()
                onCommit()
              }
              if (event.detail.key === "Escape") onCancel()
            }}
            placeholder={placeholder}
            autoFocus
          />
          <div className="cell-edit-actions">
            <Button
              variant="inline-icon"
              iconName="close"
              ariaLabel="Cancel edit"
              formAction="none"
              onClick={onCancel}
            />
            <Button
              variant="inline-icon"
              iconName="check"
              ariaLabel="Save edit"
              formAction="none"
              onClick={onCommit}
            />
          </div>
        </>
      ) : (
        <span>{value || "—"}</span>
      )}
    </Tag>
  )
}

export default VisitMetaCell
